package com.evoapps.evoshift.domain.usecase.marketplace

import com.evoapps.evoshift.domain.model.NotificationData
import com.evoapps.evoshift.domain.model.ClaimOutcome
import com.evoapps.evoshift.domain.model.Notification
import com.evoapps.evoshift.domain.model.NotificationType
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.UserPermission
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.util.AppClock.now

/**
 * A colleague claims a shift from the marketplace: a swapped-away shift or an open shift.
 *
 * The claim itself runs on the server ([ShiftRepository.claimShift]): the claimant is not the
 * shift's assignee, so Firestore rules never let them write it, and doing it in one transaction
 * also stops two people from taking the same shift. The workplace policy decides the outcome:
 * A/C hand the shift over at once, B/D park it until a manager approves; then managers are told.
 */
class TakeShiftFromPoolUseCase(
    private val shiftRepository: ShiftRepository,
    private val userRepository: UserRepository
) {
    suspend operator fun invoke(shiftId: String, candidateUserId: String): Result<ClaimOutcome> = runCatching {
        val outcome = shiftRepository.claimShift(shiftId, candidateUserId).getOrThrow()
        if (outcome == ClaimOutcome.PENDING_APPROVAL) {
            notifyManagers(shiftId, candidateUserId)
        }
        outcome
    }

    private suspend fun notifyManagers(shiftId: String, candidateUserId: String) {
        val shift = shiftRepository.getShift(shiftId).getOrNull() ?: return
        val workplaceId = RosterIds.workplaceIdOf(shift.rosterId)
        val candidate = userRepository.getUserProfile(candidateUserId).getOrNull()
        val managerRoles = setOf(UserPermission.OWNER, UserPermission.MANAGER, UserPermission.MANAGER_FINANCIAL)
        val managers = userRepository.getAssignableEmployees(workplaceId).getOrNull().orEmpty()
            .filter { it.engagements[workplaceId]?.permission in managerRoles }
        val nowStr = now().toString()
        managers.forEach { manager ->
            userRepository.sendNotification(
                Notification(
                    userId = manager.uid,
                    title = "Shift Claim Pending",
                    message = "${candidate?.displayName ?: "Someone"} wants to take a shift on ${shift.date}.",
                    type = NotificationType.SHIFT_CLAIM_PENDING,
                    createdAt = nowStr,
                    data = NotificationData.forShift(shift, workplaceId, NotificationData.NAME to candidate?.displayName, NotificationData.SUBJECT_USER_ID to candidateUserId)
                )
            )
        }
    }
}
